import React, { useState, useRef, useEffect } from 'react';
import MapGL, { Source, Layer, Popup } from 'react-map-gl';

const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

const ColorLegend = ({ colors, binEdges, units, type, region, index }) => {
    // Calculate the width for each color block
    const blockWidthPercent = 100 / colors.length;
    const labelWidthPercent = 100 / binEdges.length;
    const title = `Trend Values (${units}) \n ${type} \n ${region} - ${index}`;
  
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '10px' }}>
        {/* Color Blocks */}
        <div style={{ display: 'flex', width: '100%', height: '20px' }}>
          {colors.map((color, index) => (
            <div key={index} style={{ backgroundColor: color, width: `${blockWidthPercent}%`, height: '100%' }} />
          ))}
        </div>
  
        {/* Ticks and Labels */}
        <div style={{ display: 'flex', width: '100%' }}>
          {binEdges.map((edge, index) => {
            if (index < binEdges.length - 1) {
              let alignStyle = 'left';
              let justifyStyle = 'flex-start';
              // If it's the last index, add a tick mark at the end
              index === binEdges.length - 2 && (justifyStyle = 'space-between');
              return (
                <div key={index} style={{ width: `${blockWidthPercent}%` }}>
                  {/* Only draw tick marks between color blocks and at the ends */}
                  <div style={{ display: 'flex', flexDirection: 'row', alignItems: alignStyle, justifyContent: justifyStyle }}>
                    <div style={{ width: '2px', height: '5px', backgroundColor: 'black' }}></div>
                    {/* If it's the second-last index, draw the last tick mark */}
                    {index === binEdges.length - 2 && <div style={{ width: '2px', height: '5px', backgroundColor: 'black' }}></div>}
                  </div>
                </div>
              );
            }
          })}
        </div>
  
        {/* Labels */}
        <div style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
          {binEdges.map((edge, index) => {
            let alignStyle = 'left';
            if (index === (binEdges.length - 1) / 2) {
              alignStyle = 'center';
            } else if (index > (binEdges.length - 1) / 2) {
              alignStyle = 'right';
            }
  
            return (
              <div key={index} style={{ width: `${labelWidthPercent}%`, textAlign: alignStyle, fontSize: '12px' }}>
                {edge.toFixed(2)}
              </div>
            );
          })}
        </div>
  
        {/* Title */}
        <div style={{ textAlign: 'center', marginTop: '10px', fontSize: '14px' }}>
          <div>
            <strong>Trend Values ({units})</strong>
          </div>
          
        </div>
      </div>
    );
  };
function roundNice(number) {
  const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(number)))); // Find out the magnitude of the number
  console.log("Magnitude: ", magnitude)
  let niceNumber = Math.round(number / magnitude) * magnitude; // Round to nearest whole number at that magnitude
  
  console.log("Nice Number: ", niceNumber)
  // Additional rounding to nearest 5 or 10 if needed
  if (magnitude >= 1) {
      console.log("Magnitude >= 1")
      console.log("Number: ", number)
      console.log("Magnitude*10: ", magnitude*10)
      console.log("Number / (magnitude*10): ", number / (magnitude*10))
      console.log("Math.round(number / (magnitude*10)): ", Math.round(number / (magnitude*10)))
      console.log("Math.round(number / (magnitude*10)) * magnitude*10: ", Math.round(number / (magnitude*10)) * magnitude*10)

      //niceNumber = Math.round(number / (magnitude*10)) * magnitude*10;
      niceNumber = Math.round(niceNumber / 10) * 10;  // Nearest 10 for large magnitudes
      console.log("Nice Number: ", niceNumber)
  } else {

      // For smaller numbers, round to nearest 0.1, 0.01, etc.
      const factor = 1 / magnitude;
      niceNumber = Math.round(niceNumber * factor) / factor;
      console.log("Nice Number: ", niceNumber)
  }
  return niceNumber;
}

roundNice(-132.6)
roundNice(32.6)
roundNice(0.421)
roundNice(0.000421)
roundNice(0.8)

const TrendPlot = ({ data, viewport, setViewport, ifDecade }) => {
    if (!data || !data.trend) return null;

    const [hoverInfo, setHoverInfo] = useState(null); // To store info of the hovered square
    const mapRef = useRef();

    const { index, region } = data;
    const { slope, p_value, type } = data.trend;
    let {units } = data.trend;
    
    
        
    // if decade then change units from C/years to C/decade
    ifDecade && (units = units.replace('years', ' decade'));
   

    // if a point x (lat,lon) is in p_value, add sig: true as a field to slope
    // if a point x (lat,lon) is not in p_value, add sig: false as a field to slope
    // then use slope to plot the map
    slope.forEach((d) => {
        d.sig = p_value.some(p => p.lat === d.lat && p.lon === d.lon);
    })


    const slopeMax = Math.max(...slope.map(d => d.value));
    const slopeMin = Math.min(...slope.map(d => d.value));
    let slopeMaxAbs = Math.max(Math.abs(slopeMax), Math.abs(slopeMin));
    
    slopeMaxAbs = ifDecade ? slopeMaxAbs * 10 : slopeMaxAbs;

    // round

    // Determine if the color scale should be reversed
    const isReversed = index === 'Rx1day' || index === 'PRCPTOT';
    const numBins = 11;
    function linspace(start, end, num) {
        const step = (end - start) / (num - 1);
        let linArray = Array.from({length: num}, (_, i) => start + (step * i));
        return linArray;
    }
    const binEdges = linspaceNice(-slopeMaxAbs, slopeMaxAbs, numBins);
    function linspaceNice(start, end, numBins) {
      start = roundNice(start);
      end = roundNice(end);
  
      const range = end - start;
      const rawStep = range / (numBins - 1);
      const stepMagnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(rawStep))));
      const step = roundNice(rawStep);
  
      let binEdges = [];
      for (let i = 0; i < numBins; i++) {
          binEdges.push(start + i * rawStep);
      }
  
      // Adjust the last bin edge to exactly match the end if rounding causes slight deviation
      binEdges[binEdges.length - 1] = end;
  
      return binEdges;
  }

    // Insert a very small negative value right before zero
    // Can modify linspace to do this directly
    const zeroIndex = binEdges.indexOf(0);
    // copy the binEdges array
    
    let modifiedBinEdges = [...binEdges];
    if (zeroIndex !== -1) {
        modifiedBinEdges.splice(zeroIndex, 0, -0.00000001);
    }

    // Remove the last bin edge
    modifiedBinEdges.pop();

    console.log("Bin Edges: ", binEdges)
    const RdBuColorMap = ["#67001f",
        "#b6202f",
        "#dd6f59",
        "#f7b799",
        "#fae7dc",
        "#e2edf3",
        "#a7d0e4",
        "#559ec9",
        "#256baf",
        "#053061",
        ]

    const colors = isReversed ? RdBuColorMap : RdBuColorMap.reverse();
    

    let colorMap = [];
    // Start with 1 since the first color is for values less than the first edge
    for (let i = 1; i < binEdges.length - 1; i++) {
        
        colorMap.push(binEdges[i], colors[i]);
    }
    

    // Assuming you have a function to convert each slope point to a square (polygon) in GeoJSON format
    const trend_map = {
        type: 'FeatureCollection',
        features: slope.map((d) => convertToPointSquare(d))
    };

    const p_value_map = {
        type: 'FeatureCollection',
        features: p_value.map(d => ({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: [d.lon + 0.125, d.lat - 0.125] // 0.125 = .25/2 or res/2
            },
            properties: {
                sig: true
            }
        }))
    };
    const [showGrid, setShowGrid] = useState(true);
    console.log("colorMap: ", colorMap)
    console.log("RdBuColorMap[0]: ", RdBuColorMap[0])
    console.log("colorMap.flat(): ", colorMap.flat())
    const squareLayer = {
        id: 'trend-layer',
        type: 'fill',
        paint: {
            'fill-opacity': 0.9,
            'fill-color': [
                'step',
                ['get', 'value'],
                RdBuColorMap[0], // Default color for values less than the first bin edge
                ...colorMap.flat() // Ensure this array has an even number of elements
                
        
        ]
        }
    };
    
    const circleLayer = {
        id: 'p-value-layer',
        type: 'circle',
        paint: {
            'circle-radius': [
                'interpolate',
                ['linear'],
                ['zoom'],
                3, 1,
                4, 2,
                4.5, 2.5,
                4.75, 2.75,
                5.25, 3,      
            ],
            'circle-color': 'rgb(0,0,0)',
            'circle-opacity': 0.8
        }
    };


    // Function to convert slope point to square polygon
    // THIS FUNCTION CAN BE GENERALISED TO DIFFERENT RESOLUTION GRIDS
    // FOR THIS WE NEED TO HAVE THE RESOLUTION SUPPLIED IN THE DATA
    function convertToPointSquare(d) {
        // Calculate the corners of the square polygon
        const topLeft = [d.lon, d.lat];
        const topRight = [d.lon + 0.25, d.lat];
        const bottomRight = [d.lon + 0.25, d.lat - 0.25];
        const bottomLeft = [d.lon, d.lat - 0.25];
        const value = ifDecade ? d.value * 10 : d.value;
        return {
            type: 'Feature',
            geometry: {
                type: 'Polygon',
                // Make sure to close the polygon by repeating the first coordinate at the end
                coordinates: [[topLeft, topRight, bottomRight, bottomLeft, topLeft]]
            },
            properties: {
                value: value,
                sig: d.sig
            }
        };
    }

    // Handle hover over the squares
    const onHover = event => {
        const { features, point } = event;
        const hoveredFeature = features && features.find(f => f.layer.id === 'trend-layer');

        setHoverInfo(hoveredFeature ? { feature: hoveredFeature, x: point.x, y: point.y } : null);
    };

    return (
        <div id="dashboard-map-plot">
            <MapGL
                {...viewport}
                width="100%"
                height={500}
                mapStyle="mapbox://styles/mapbox/light-v11"
                mapboxApiAccessToken={MAPBOX_ACCESS_TOKEN}
                onViewportChange={setViewport}
                onHover={onHover}
                ref={mapRef}
            >
              <div style={{ position: 'absolute', right: 0, top: 0, padding: '10px' }}>
                <button onClick={() => setShowGrid(!showGrid)}>{showGrid ? 'Hide Grid' : 'Show Grid'}</button>
              </div>
                <Source type="geojson" data={trend_map}>
                    <Layer {...squareLayer} />
                </Source>
                {showGrid && (
                <Source type="geojson" data={p_value_map}>
                    <Layer {...circleLayer} />
                </Source>
                )}
                {hoverInfo && (
                    
                    <Popup
                    latitude={hoverInfo.feature.geometry.coordinates[0][0][1] - 0.125}
                    longitude={hoverInfo.feature.geometry.coordinates[0][0][0] + 0.125}
                    closeButton={false}
                    closeOnClick={false}
                    
                >
                        <div className='custom-tooltip'>
                            <p>
                            <strong>Slope: </strong> {Number(hoverInfo.feature.properties.value).toFixed(2)} {units}
                            </p>
                            <p><i>{hoverInfo.feature.properties.sig ? 'Statistically Significant' : ''}</i></p>
                        </div>
                    </Popup>
                )}
            </MapGL>
            <ColorLegend colors={colors} binEdges={linspaceNice(-slopeMaxAbs, slopeMaxAbs, numBins)} units={units} type={type} region={region} index={index} />
        </div>
    );
};

export default TrendPlot;